import { useCallback, useContext, useEffect } from 'react';
import { useRouteMatch } from 'react-router-dom';

import { isNil } from 'lodash';
import mixpanel from 'mixpanel-browser';

import UserContext from 'components/UserContext';

import { trackMixpanelEventSdk } from './sdk';

const MIXPANEL_KEY = process.env.REACT_APP_MIXPANEL_KEY;

export const trackMixpanelEvent = (userId, eventName, eventData = {}) => {
  if (!isNil(MIXPANEL_KEY)) {
    mixpanel.track(eventName, { ...eventData, userId });
  }
};

export const trackMixpanelPageView = (userId, eventData = {}) => {
  if (!isNil(MIXPANEL_KEY)) {
    mixpanel.track_pageview({ ...eventData, userId });
  }
};

export const useMixpanelPageOpenTrack = (eventName) => {
  const { user } = useContext(UserContext);

  useEffect(() => {
    trackMixpanelEventSdk({
      user_id: user.id,
      event_name: eventName
    });
  }, [user.id, eventName]);
};

// Helper utility to safely read cookies
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

export const getClarityUserId = () => {
  const clarityCookie = getCookie('_clck');

  if (clarityCookie) {
    const decodedCookie = decodeURIComponent(clarityCookie);
    const clarityUserId = decodedCookie.split('^')[0];

    return clarityUserId;
  } else {
    // Fails silently for EU users without the cookie
    return null;
  }
};

/**
 * Centralized Mixpanel tracking hook.
 * Automatically injects global properties (user, organization, url, etc.) into every event.
 *
 * @example
 * // 1. Initialize at the top of your component
 * const { trackEvent } = useEdLightTracking({ feature: 'assignments', view: 'math_create' });
 *
 * // 2. Call it in your handlers
 * trackEvent('assignment_created', { assignment_type: 'template' });
 */
export const useEdLightTracking = ({
  feature,
  view,
  customRouteConstant,
  ...additionalGlobals
} = {}) => {
  const { user } = useContext(UserContext);
  const match = useRouteMatch();

  const trackEvent = useCallback(
    (eventName, specificProperties = {}) => {
      const routePath = customRouteConstant || match.path;
      const normalizedURL = process.env.REACT_APP_BASE_FRONTEND_URL + routePath;

      const globalProperties = {
        feature,
        view,
        user_id: user?.id,
        user_role: user?.role,
        organization_id: user?.organization_id,
        organization_name: user?.organization_name,
        normalizedURL,
        ...additionalGlobals
      };

      trackMixpanelEvent(user?.id, eventName, {
        ...globalProperties,
        ...specificProperties
      });
    },
    [user, feature, view, match.path, customRouteConstant, additionalGlobals]
  );

  return { trackEvent };
};
